import React, { useMemo } from 'react';
import {
  FlexCol,
  FlexRow,
  BasicContainer,
  ButtonAsWrapper,
  BodyTextSmall,
  DetailTextLarge,
  ContainerStyleProps,
} from '@minecraft.atoms';
import Icon from '@minecraft.icon';
import { TabbedComponentProps, validateTabsProp } from './utils';

const getTextColor = (disabled: boolean, active: boolean) => {
  if (disabled) return 'lowContrastText';
  if (active) return 'default';
  return 'disabled';
};

type TabbedContainerProps = {
  bgColor?: ContainerStyleProps['bgColor'];
};

export const TabbedContainer = <TabKey extends string>({
  activeTab,
  'data-qa-id': dataQaId,
  'data-testid': dataTestId,
  onTabChange,
  tabs,
  bgColor = 'card',
}: TabbedComponentProps<TabKey> & TabbedContainerProps) => {
  const tabsToUse = useMemo(() => validateTabsProp({ tabs, componentName: 'TabbedContainer' }), [tabs]);
  return (
    <FlexCol data-qa-id={dataQaId} data-testid={dataTestId} bgColor={bgColor} w="full">
      <FlexRow
        role="tablist"
        className="cn_overflow-x-auto cn_atom_pt-1 cn_atom_px-1"
        alignItems="flex-end"
        wrap="nowrap"
        w="full"
        gap="1"
      >
        {tabsToUse.map((tab) => {
          const isActive = tab.key === activeTab;
          return (
            <ButtonAsWrapper
              aria-controls={`${tab.key}-tabpanel`}
              aria-label={tab.tabTitle}
              aria-selected={isActive}
              aria-disabled={tab?.disabled}
              data-qa-id={`${tab.key}-tab`}
              disabled={isActive || tab?.disabled}
              id={`${tab.key}-tabbutton`}
              key={tab.key}
              onClick={() => onTabChange(tab.key)}
              role="tab"
              className="cn_atom_rounded-t-lg"
              hoverBgColor={!tab?.disabled ? 'tabHeaderHover' : 'transparent'}
            >
              <FlexRow
                wrap="nowrap"
                key={tab.key}
                className="cn_atom_px-4 cn_atom_py-2"
                alignItems="center"
                gap="3"
                bgColor={isActive ? 'tabActive' : 'transparent'}
                customBordersRadius={{ topLeft: 0.5, topRight: 0.5 }}
                textColor={getTextColor(tab?.disabled, isActive)}
              >
                {tab?.tabIconName && <Icon name={tab.tabIconName} size="mmd" inheritColor />}
                <FlexCol>
                  <BodyTextSmall noWrap fontWeight={isActive ? 'semibold' : 'medium'} color="inherit">
                    {tab.tabTitle}
                  </BodyTextSmall>
                  {tab?.tabSubTitle && (
                    <DetailTextLarge noWrap fontWeight={isActive ? 'semibold' : 'medium'} color="inherit">
                      {tab.tabSubTitle}
                    </DetailTextLarge>
                  )}
                </FlexCol>
              </FlexRow>
            </ButtonAsWrapper>
          );
        })}
      </FlexRow>
      <BasicContainer className="cn_atom_px-1" w="full">
        {tabsToUse.map((tab) => (
          <BasicContainer
            aria-labelledby={`${tab.key}-tabbutton`}
            hidden={tab.key !== activeTab}
            id={`${tab.key}-tabpanel`}
            key={tab.key}
            role="tabpanel"
            w="full"
            bgColor="tabActive"
            customBordersRadius={{ bottomLeft: 0.5, bottomRight: 0.5, topLeft: 0, topRight: 0 }}
          >
            {tab.Component}
          </BasicContainer>
        ))}
      </BasicContainer>
    </FlexCol>
  );
};
